<script setup lang="ts">
import { Components } from '@/api/generated/api';
import CheckMarkIcon from '@/assets/icons/CheckMarkIcon.vue';
import SchoolSubjectBadge from '@/core/elements/SchoolSubjectBadge.vue';
import { ISelectOption } from '@/interfaces';
import { matches } from 'common/utils/string.utils';
import { computed, toRefs } from 'vue';

const emit = defineEmits<{
  'update:modelValue': [value: number[]];
  'close-menu': [];
}>();

const props = defineProps<{
  options: ISelectOption<Components.Schemas.SchoolSubjectDto>[];
  modelValue: number[];
  searchQuery: string;
  canSelectMultiple?: boolean;
}>();
const { options, modelValue, searchQuery, canSelectMultiple } = toRefs(props);

const visibleOptions = computed<ISelectOption<Components.Schemas.SchoolSubjectDto>[]>(() => {
  const query = searchQuery.value.trim().toLowerCase();
  return options.value.filter((o) => matches(o.label, query));
});

const closeMenu = (): void => {
  emit('close-menu');
};

const selectOption = (option: ISelectOption<Components.Schemas.SchoolSubjectDto> | null): void => {
  let newModelValue: number[] = [];
  if (option) {
    if (canSelectMultiple.value) {
      newModelValue = modelValue.value.includes(option.value.id)
        ? modelValue.value.filter((id) => id !== option.value.id)
        : [...modelValue.value, option.value.id];
    } else {
      newModelValue = [option.value.id];
    }
  }
  emit('update:modelValue', newModelValue);
};

const isSelected = (option: ISelectOption<Components.Schemas.SchoolSubjectDto>): boolean => {
  return modelValue.value.includes(option.value.id);
};

const handleOptionClick = (option: ISelectOption<Components.Schemas.SchoolSubjectDto>): void => {
  selectOption(option);
};
</script>

<template>
  <ul
    :class="['bg-white focus:outline-none rounded-md', 'w-full', 'flex flex-col gap-xs ']"
    role="listbox"
    @keydown.esc="closeMenu"
  >
    <li
      v-for="option in visibleOptions"
      :key="option.value.id"
      :aria-selected="isSelected(option) ? 'true' : false"
      role="option"
      tabindex="0"
      :class="['flex items-center gap-sm', 'dropdown-option', 'focus:outline-none focus:bg-surface-tertiary']"
      @click="handleOptionClick(option)"
      @keypress.space="handleOptionClick(option)"
      @keydown.enter.prevent="handleOptionClick(option)"
    >
      <SchoolSubjectBadge :school-subject="option.value" />
      <span>{{ option.label }}</span>
      <CheckMarkIcon v-if="isSelected(option)" :class="['size-cell-icon shrink-0', 'text-tint', 'ml-auto']" />
    </li>
  </ul>
</template>
